import { Button, Drawer, Icon } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { MarketOverviewWithNews, SingleNews } from '@toggle/toggle';
import clsx from 'clsx';
import { toZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatDeepAnalysis } from '~/components/chat/chat-input/chat-deep-analysis/ChatDeepAnalysis';
import { useMarketInsightsDrawer } from '~/hooks/use-market-insights-drawer/useMarketInsightsDrawer';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';
import { ArticleDrawerContent } from '~/widgets/news-widget/ArticleDrawerContent';

import { getMarketInsightTitle } from '../market-insight-card/utils';
import styles from './MarketInsightDrawer.module.scss';

export const MarketInsightDrawer = ({
  activeInsightsGroup,
}: {
  activeInsightsGroup: MarketOverviewWithNews[];
}) => {
  const { t } = useTranslation([
    'whatsNew',
    'news',
    'chart',
    'analyze',
    'countries',
  ]);
  const [selectedNews, setSelectedNews] = useState<SingleNews | undefined>();
  const [activeArticleId, setActiveArticleId] = useState<string>('');

  useDisableScroll();
  const { navigateToConversation } = useChatNavigation();
  const { closeDrawer } = useMarketInsightsDrawer();
  const { trackAlfredQuerySent } = usePosthogEvents();
  const { postQuestion, analysisMode } = useChat(state => ({
    postQuestion: state.postQuestion,
    analysisMode: state.analysisMode,
  }));

  const onNewsPillClick = (news: SingleNews) => {
    setSelectedNews(news);
    setActiveArticleId(news.id);
  };

  const onGeneratedQuestionClick = async (question: string) => {
    const conversationId = (await postQuestion(question)) ?? '';
    navigateToConversation(conversationId, {
      trackingSource: POSTHOG_TRACKING_SOURCES.DASHBOARD_WHATS_NEW,
    });

    trackAlfredQuerySent({
      source: POSTHOG_TRACKING_SOURCES.MARKET_SUMMARY_DRAWER,
      type: analysisMode,
      via: POSTHOG_TRACKING_SOURCES.DASHBOARD_WHATS_NEW,
      isAutoGenerated: true,
      value: question,
      conversationId,
    });
  };

  const onCloseClick = () => {
    if (!!selectedNews) {
      setSelectedNews(undefined);
    } else {
      closeDrawer();
    }
  };

  const onBackgroundClick = () => {
    closeDrawer();
    setSelectedNews(undefined);
    setActiveArticleId('');
  };

  return (
    <Drawer
      className={clsx(styles.styledDrawer, {
        [styles.styledDrawerExpanded]: !!selectedNews,
      })}
      isOpen
      handleCloseClick={onCloseClick}
      customBackgroundClickHandler={onBackgroundClick}
    >
      <div className={styles.root}>
        <div className={styles.drawersWrapper}>
          <div className={styles.marketInsightsWrapper}>
            {activeInsightsGroup.map((insight, idx) => {
              const isLastItem = idx === activeInsightsGroup.length - 1;
              const isFirstItem = idx === 0;
              const zonedTime = toZonedTime(
                new Date(insight.date),
                'America/New_York'
              );
              return (
                <React.Fragment key={insight.id}>
                  <div className={styles.insightWrapper}>
                    {isFirstItem && (
                      <div className={styles.headerColumn}>
                        <div className={styles.headerTitle}>
                          {t('whatsNew:marketSummary')}
                        </div>
                        <div className={styles.headerSubtitle}>
                          {new Intl.DateTimeFormat(navigator.language, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          }).format(zonedTime)}
                        </div>
                      </div>
                    )}
                    <div className={styles.summaryWrapper}>
                      <div className={styles.summaryTitle}>
                        {getMarketInsightTitle(insight.market_hours, t).title}
                      </div>
                      <div className={styles.summaryText}>
                        {insight.summary}
                      </div>
                      {!!insight.articles.length && (
                        <>
                          <div className={styles.readMoreLabel}>
                            {/* i18next-extract-mark-context-next-line ["benzinga", "mtn", "dowjones"] */}
                            {t('whatsNew:readMoreFrom', {
                              source: t('news:source', {
                                context: insight.articles[0].source,
                              }),
                            })}
                          </div>
                          <div className={styles.newsPillsWrapper}>
                            {insight.articles.map(news => (
                              <div
                                onClick={() => onNewsPillClick(news)}
                                key={news.id}
                                className={clsx(
                                  styles.newsPill,
                                  styles.newsPillDashboard
                                )}
                              >
                                <div className={styles.newsPillText}>
                                  {news.title}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <div className={styles.alfredSectionWrapper}>
                      <div className={styles.horizontalDivider} />
                      <div className={styles.alfredSectionTitle}>
                        <Icon
                          iconName="Alfred"
                          size={20}
                          fillColor="var(--icon-primary)"
                        />
                        <span>{t('whatsNew:whatAmIMissing')}</span>
                      </div>
                      <div className={styles.alfredQuestionsWrapper}>
                        <div className={styles.styledChatInput}>
                          <ChatDeepAnalysis
                            config={{
                              placeholder: t('whatsNew:askAFollowUp'),
                              allowedModes: ['research'],
                              disableContextHeader: true,
                              autoFocus: false,
                            }}
                            trackingMetadata={{
                              source:
                                POSTHOG_TRACKING_SOURCES.MARKET_SUMMARY_DRAWER,
                              via: POSTHOG_TRACKING_SOURCES.DASHBOARD_WHATS_NEW,
                            }}
                          />
                        </div>
                        {insight.questions.length > 0 && (
                          <div className={styles.generatedQuestionsWrapper}>
                            {insight.questions.map(question => (
                              <div
                                className={styles.generatedQuestionWrapper}
                                key={question}
                                onClick={() =>
                                  onGeneratedQuestionClick(question)
                                }
                              >
                                <span>{question}</span>
                                <Button iconName="ArrowUp" size="small" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {!isLastItem && (
                    <div>
                      <div className={styles.marketInsightSeparator} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {!!selectedNews && (
            <div className={styles.articleWrapper}>
              <div className={styles.verticalDivider} />
              <ArticleDrawerContent
                newsId={activeArticleId}
                data={selectedNews}
              />
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
